/**
 * User Session Cleanup Utilities
 *
 * Reusable functions for cleaning up user session data (localStorage, cookies, Redux state)
 * without requiring API calls to logout endpoint.
 *
 * Use cases:
 * - New auth flow: ProfileSecurityStatus=0 received, need to clean up session
 * - Manual logout without API call
 * - Session expired scenarios
 */

import Storage from 'utils/localStorage/Storage';
import Flush from 'utils/localStorage/Flush';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import { KEYS as GENAI_LOCAL_STORAGE } from 'ai/utils/sessionStorage';
import { KEYS as GENAI_LYRA_LOCAL_STORAGE } from 'ai/utils/lyraAdviceCache';
import cookieUtils from 'utils/Cookies';
import RCPSCookies from 'utils/RCPSCookies';
import authenticationUtils from 'utils/Authentication';
import { PROFILE_STATUS_COOKIE_NAME, IS_SSI_COOKIE_NAME } from 'services/auth/newFlow/constants';
import targetersActions from 'actions/targetersActions';

const LITHIUM_SESSION_KEY_COOKIE_NAME = 'LiSESSIONID';

const { isAuthServiceEnabled } = authenticationUtils;

/**
 * Flush all user-related data from cache/localStorage
 */
export const flushUserData = () => {
    Flush.flushUser();
    Flush.flushBiAccountAndProfileId();
    Flush.flushBasket();
    Flush.flushUserAdditionalData();
    Flush.flushMyListsData();
    Flush.flushLoginProfileWarnings();
    Flush.flushP13nData();
    Flush.flushBeautyOffers();

    if (isAuthServiceEnabled()) {
        Flush.flushAuthTokens();
    }
};

/**
 * Clear user-related cookies
 */
export const clearUserCookies = () => {
    // Set lithium session key to 0, to mimic delete
    cookieUtils.write(LITHIUM_SESSION_KEY_COOKIE_NAME, 0);
    cookieUtils.write(cookieUtils.KEYS.PREVIEW_CUSTOMER, 0);

    // Clear new auth flow readable cookies
    if (RCPSCookies.isRCPSCookieBasedAuthEnabled()) {
        cookieUtils.delete(PROFILE_STATUS_COOKIE_NAME);
        cookieUtils.delete(IS_SSI_COOKIE_NAME);
        cookieUtils.delete('isSSI'); // Legacy name
        cookieUtils.delete('profileStatus'); // Legacy name
    }
};

/**
 * Clear user-related storage (localStorage and sessionStorage)
 * @param {Function} dispatch - Redux dispatch function (optional, for clearing targeters cache)
 */
export const clearUserStorage = dispatch => {
    const { local, session } = Storage;

    local.removeItem('jStorage');
    local.removeItem('jStorage_update');
    local.removeItem(LOCAL_STORAGE.STAY_SIGN_IN);
    local.removeItem(GENAI_LOCAL_STORAGE.GENAI_ANONYMOUS_ID_KEY);
    local.removeItem(GENAI_LOCAL_STORAGE.GENAI_SESSION_KEY);
    local.removeItem(GENAI_LOCAL_STORAGE.GENAI_LAST_FOLLOW_UPS_KEY);
    local.removeItem(GENAI_LYRA_LOCAL_STORAGE.LYRA_ADVICES_BY_CAMPAIGN);
    session.removeItem('selectedStore');
    session.removeItem('lastDataForBraze');
    session.removeItem('preferredZipCode');
    session.removeItem('maxAddressValidationCount');
    session.removeItem(LOCAL_STORAGE.DISABLE_BEAUTY_PREFERENCES_SPOKE);

    if (dispatch) {
        dispatch(targetersActions.flushTargeterCache());
    }
};

/**
 * Complete user session cleanup (flush + clear cookies + clear storage)
 * Use this when you need to clean up user session without calling logout API
 * @param {Function} dispatch - Redux dispatch function (optional, for clearing targeters cache)
 */
export const cleanupUserSession = dispatch => {
    flushUserData();
    clearUserCookies();
    clearUserStorage(dispatch);

    Sephora.logger.verbose('[UserSessionCleanup] Complete user session cleanup executed');
};

export default {
    flushUserData,
    clearUserCookies,
    clearUserStorage,
    cleanupUserSession
};
